import {
  Component,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { take } from 'rxjs';
import {
  ISelfPayRateItem,
  SelfPayRateType,
  SelfPayRatesHttpService,
  SelfPayVisitType
} from '@sondermind/data-access/client-insurance';
import {
  CurrentUserSegmentEvents,
  ICurrentUserSegmentSchema
} from '@sondermind/data-access/client-user';
import { Logger } from '@sondermind/datadog-logging';
import { SegmentService } from '@sondermind/segment';
import { IrisButtonContainerAlignments, IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IconColors, IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisEmptyStateModule } from '@sondermind/iris/empty-state';
import { IrisSpinnerComponent } from '@sondermind/iris/spinner';

import { SelfPayCostEstimateDetailedComponent } from '../self-pay-cost-estimate-detailed/self-pay-cost-estimate-detailed.component';
import { ISelfPayFeaturedEstimate } from '../../models/cost-estimation.model';

const FEATURED_LABEL_TALK_THERAPY_INTAKE = 'Talk therapy\n60 minutes intake';
const FEATURED_LABEL_PSYCHIATRY_INTAKE = 'Medication management\nInitial evaluation';

@Component({
  selector: 'client-self-pay-cost-estimation',
  templateUrl: './self-pay-cost-estimation.component.html',
  styleUrls: ['./self-pay-cost-estimation.component.scss'],
  imports: [
    CommonModule,
    SelfPayCostEstimateDetailedComponent,
    IrisButtonModule,
    IrisEmptyStateModule,
    IrisIconComponent,
    IrisSpinnerComponent
  ]
})
export class SelfPayCostEstimationComponent implements OnInit {
  readonly fromDirectBooking = input<boolean>(false);
  readonly personaClientId = input.required<string | number>();
  readonly costEstimationReviewed = output<void>();

  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  readonly featuredEstimates = signal<ISelfPayFeaturedEstimate[]>([]);
  readonly rates = signal<ISelfPayRateItem[]>([]);
  readonly ratesLoading = signal<boolean>(false);
  readonly ratesLoadError = signal<boolean>(false);

  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisIconSizes = IconSizes;
  IconColors = IconColors;

  constructor(
    private readonly logger: Logger,
    private readonly segmentService: SegmentService<ICurrentUserSegmentSchema>,
    private readonly selfPayRatesHttp: SelfPayRatesHttpService
  ) {}

  ngOnInit(): void {
    this.loadRates();
  }

  private loadRates(): void {
    if (this.fromDirectBooking()) {
      // TODO: load provider-specific rates in CASH-1774
      return;
    }

    this.ratesLoadError.set(false);
    this.ratesLoading.set(true);
    this.selfPayRatesHttp
      .getSelfPayRates$(SelfPayVisitType.TELEHEALTH)
      .pipe(take(1))
      .subscribe({
        next: (rates) => {
          this.ratesLoading.set(false);
          if (rates.length === 0) {
            this.reviewedCostEstimation();
            return;
          }
          this.rates.set(rates);
          const featured = this.buildNonSchedulingFeaturedEstimatesFromRates(rates);
          this.featuredEstimates.set(featured);
          this.trackSelfPayCostEstimateShown(featured);
        },
        error: (err: unknown) => {
          this.logger.error('Self-pay rates failed to load', { err });
          this.ratesLoadError.set(true);
          this.ratesLoading.set(false);
        }
      });
  }

  /**
   * Build featured estimates from API rates: only TALK_THERAPY_INTAKE and PSYCHIATRY_INTAKE,
   * with fixed labels. For non-direct-booking context only. API always returns all 5 rate types.
   */
  private buildNonSchedulingFeaturedEstimatesFromRates(rates: ISelfPayRateItem[]): ISelfPayFeaturedEstimate[] {
    const talkTherapyIntake = rates.find((r) => r.type === SelfPayRateType.TALK_THERAPY_INTAKE);
    const psychiatryIntake = rates.find((r) => r.type === SelfPayRateType.PSYCHIATRY_INTAKE);

    if (!talkTherapyIntake?.amount || !psychiatryIntake?.amount) {
      return [];
    }

    return [
      { serviceLabel: FEATURED_LABEL_TALK_THERAPY_INTAKE, amount: talkTherapyIntake.amount.amount },
      { serviceLabel: FEATURED_LABEL_PSYCHIATRY_INTAKE, amount: psychiatryIntake.amount.amount }
    ];
  }

  reviewedCostEstimation(): void {
    this.costEstimationReviewed.emit();
  }

  private trackSelfPayCostEstimateShown(featuredEstimates: ISelfPayFeaturedEstimate[]): void {
    this.segmentService
      .track<CurrentUserSegmentEvents.SELF_PAY_COST_ESTIMATE_SHOWN>(
      CurrentUserSegmentEvents.SELF_PAY_COST_ESTIMATE_SHOWN,
      {
        fromDirectBooking: this.fromDirectBooking(),
        featuredEstimates: featuredEstimates.map((e) => ({
          serviceLabel: e.serviceLabel,
          amount: e.amount
        })),
        personaClientId: String(this.personaClientId())
      }
    )
      .catch(() => {});
  }
}
